/**
 * OfacRubricRunner — Admin → System → "OFAC Rubric (Dev)".
 *
 * Bill 2026-05-09 — DEV-only diagnostic that exercises the production wire
 * (App → Function → JHA → Function → App, then Function → Treasury SLS) by
 * running a JSON rubric of test names. For each test:
 *
 *   1. POST /jxrl/Customer/OFACSrch?Count=N  (via client.jxrl.ofacSrch)
 *   2. For each AliasId in OFACSrchArray, GET /v3/dev/ofac-sls/{aliasId}
 *      (via client.retail.dev.ofacSls)
 *   3. Score: PASS (SLS entity returned + JHA AliasName matches an SLS name)
 *             / PARTIAL (entity returned but name doesn't match)
 *             / FAIL (no SLS entity returned)
 *
 * Server-side gates: SuperAdmin role + `dev.tools.enabled` SystemSetting.
 * Production never sees this surface unless an admin has flipped the flag.
 * (Note: `dev.tools.enabled` is decoupled from `dev.mode.enabled` — flipping
 * it on does NOT force every cart into Sandbox the way dev.mode.enabled does.)
 *
 * Sample rubric JSON shape:
 *   {
 *     "name": "OFAC enrichment rubric v1",
 *     "tests": [
 *       { "id": "t1", "comName": "Mohammad Daoud Muzzamil", "comNameType": "Indv",
 *         "expectAliasIdContains": [25400],
 *         "notes": "Known SDGT entry uid 25400 (per legacy SDN.XML)." }
 *     ]
 *   }
 */

import { useCallback, useMemo, useRef, useState } from 'react';
import type { CSSProperties, DragEvent, ChangeEvent, ReactNode } from 'react';
import { useApiClient } from '@/contexts/ApiClientContext';
import type { OfacSlsLookupResponse, OfacSlsName } from '@retail-ops/api-client';

// ── Local primitive shims (sysops CSS vocab) ───────────────────────────────
// Relocated from ROP-teams Admin 2026-06-18 (BACKLOG-0579). The source used the
// ROP-teams primitives (Banner/Button/Pill + CrudPage/CrudHeader/CrudToastBanner);
// here we render the same shapes with sysops' globals.css classes so the bulk of
// the component body is unchanged.
type Tone = 'ok' | 'warn' | 'err' | 'info' | 'muted';

function Banner({ tone = 'info', title, children }: { tone?: Tone; title?: string; children: ReactNode }): JSX.Element {
  const cls = tone === 'err' ? 'banner banner--err' : tone === 'warn' ? 'banner banner--warn' : 'banner banner--info';
  return (
    <div className={cls} {...(tone === 'err' ? { role: 'alert' as const } : {})}>
      {title ? <strong>{title}: </strong> : null}
      {children}
    </div>
  );
}

function Button({
  variant = 'ghost', size, disabled, onClick, children,
}: {
  variant?: 'primary' | 'ghost' | 'danger';
  size?: 'sm';
  disabled?: boolean;
  onClick?: () => void;
  children: ReactNode;
}): JSX.Element {
  const cls = ['btn', `btn--${variant}`, size === 'sm' ? 'btn--sm' : ''].filter(Boolean).join(' ');
  return (
    <button type="button" className={cls} disabled={disabled} onClick={onClick}>
      {children}
    </button>
  );
}

function Pill({ variant = 'muted', children }: { variant?: Tone; children: ReactNode }): JSX.Element {
  return <span className={`pill pill--${variant}`}>{children}</span>;
}

function CrudPage({ children }: { children: ReactNode }): JSX.Element {
  return <div className="ofac-rubric-page">{children}</div>;
}

function CrudHeader({
  title, intro, badge, actions,
}: {
  title: string;
  intro: string;
  badge?: { tone: Tone; label: string };
  actions?: ReactNode;
}): JSX.Element {
  return (
    <header className="page-header" style={{ display: 'flex', alignItems: 'flex-start', gap: 16, flexWrap: 'wrap' }}>
      <div style={{ flex: 1, minWidth: 280 }}>
        <h1 style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          {title}
          {badge ? <Pill variant={badge.tone}>{badge.label}</Pill> : null}
        </h1>
        <p className="page-intro">{intro}</p>
      </div>
      {actions ? <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>{actions}</div> : null}
    </header>
  );
}

type CrudToast = { tone: 'ok' | 'warn' | 'err' | 'info'; text: string };

function CrudToastBanner({ toast, onDismiss }: { toast: CrudToast | null; onDismiss: () => void }): JSX.Element | null {
  if (!toast) return null;
  const tone: Tone = toast.tone === 'ok' ? 'info' : toast.tone;
  return (
    <div className={`banner banner--${tone === 'err' ? 'err' : tone === 'warn' ? 'warn' : 'info'}`} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
      <span style={{ flex: 1 }}>{toast.text}</span>
      <Button variant="ghost" size="sm" onClick={onDismiss}>Dismiss</Button>
    </div>
  );
}

// ── Rubric input shape ─────────────────────────────────────────────────────

interface RubricTest {
  id?: string;
  comName: string;
  comNameType?: 'Indv' | 'Org';
  /** Optional — if present, the runner asserts at least one AliasId in the
   *  JHA response equals one of these values. Helpful for known-target tests. */
  expectAliasIdContains?: number[];
  /** Optional — at least one match's primary or alias name must contain this
   *  (case-insensitive) for the test to PASS. */
  expectNameContains?: string;
  notes?: string;
}

interface Rubric {
  name?: string;
  tests: RubricTest[];
}

// ── Result rows ────────────────────────────────────────────────────────────

type Verdict = 'PASS' | 'PARTIAL' | 'FAIL' | 'PENDING' | 'ERROR';
type NameMatch = 'EXACT' | 'CONTAINS' | 'NONE';

interface MatchRow {
  testId: string;
  comName: string;
  jhaAliasId: string;
  jhaAliasName: string;
  jhaMatchType: string;
  slsFound: boolean;
  slsPrimaryName: string;
  slsAliasNames: string;
  slsPrograms: string;
  slsDob: string;
  slsGender: string;
  slsPlaceOfBirth: string;
  nameMatch: NameMatch;
  verdict: Verdict;
  error?: string;
}

interface TestResult {
  testId: string;
  comName: string;
  jhaHits: number;
  /**
   * Bill 2026-05-09 — JHA's OFACSrch response carries TWO row shapes per
   * match. "OFACAlias" rows have an AliasId (a real candidate we can resolve
   * against Treasury SLS); "OFACName" / no-AliasId rows are summary rows.
   * The rubric runner only scores OFACAlias rows; summary rows are counted
   * here so we don't mistake them for FAILs.
   */
  jhaSummaryRowsSkipped: number;
  matches: MatchRow[];
  /** When JHA itself failed (auth / 5xx / network). */
  jhaError?: string;
  expectedAliasIdMet?: boolean;
  expectedNameMet?: boolean;
  overall: Verdict;
}

interface RubricRun {
  startedAt: string;
  finishedAt?: string;
  rubricName: string;
  results: TestResult[];
  summary: {
    total: number;
    pass: number;
    partial: number;
    fail: number;
    error: number;
    durationMs: number;
  };
}

// ── Sample rubric ──────────────────────────────────────────────────────────

const SAMPLE_RUBRIC: Rubric = {
  name: 'OFAC enrichment rubric v1 — baseline',
  tests: [
    {
      id: 't1',
      comName: 'Mohammad Daoud Muzzamil',
      comNameType: 'Indv',
      expectAliasIdContains: [25400],
      expectNameContains: 'MUZZAMIL',
      notes: 'Known SDGT entry uid 25400 — should be a clean PASS.',
    },
    {
      id: 't2',
      comName: 'Vladimir Putin',
      comNameType: 'Indv',
      expectNameContains: 'PUTIN',
      notes: 'Multiple Russia program entries — exercises primary-name path.',
    },
    {
      id: 't3',
      comName: 'Maduro Moros',
      comNameType: 'Indv',
      expectNameContains: 'MADURO',
      notes: 'Venezuela program target.',
    },
    {
      id: 't4',
      comName: 'Dad4626 Dad Test4626',
      comNameType: 'Indv',
      notes: "Bill's test data — known fuzzy false-positive. Any clean SLS entity = PASS.",
    },
    {
      id: 't5',
      comName: 'John Smith',
      comNameType: 'Indv',
      notes: 'Common-name flood — tests pagination + multi-AliasId behavior.',
    },
    {
      id: 't6',
      comName: 'Abu Mohammad',
      comNameType: 'Indv',
      notes: 'Short-token fuzzy — many AKA matches expected.',
    },
  ],
};

// ── Helpers ────────────────────────────────────────────────────────────────

function normalizeName(s: string | null | undefined): string {
  return (s ?? '')
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .trim();
}

function classifyNameMatch(jhaName: string, slsNames: ReadonlyArray<OfacSlsName>): NameMatch {
  const target = normalizeName(jhaName);
  if (!target) return 'NONE';
  for (const n of slsNames) {
    const candidates = [n.fullName, n.firstName, n.lastName, `${n.firstName ?? ''} ${n.lastName ?? ''}`];
    for (const cand of candidates) {
      const norm = normalizeName(cand);
      if (!norm) continue;
      if (norm === target) return 'EXACT';
    }
  }
  // Substring fallback
  for (const n of slsNames) {
    const candidates = [n.fullName, n.firstName, n.lastName];
    for (const cand of candidates) {
      const norm = normalizeName(cand);
      if (!norm) continue;
      if (norm.includes(target) || target.includes(norm)) return 'CONTAINS';
    }
  }
  return 'NONE';
}

function csvCell(s: string | number | undefined | null): string {
  const v = s == null ? '' : String(s);
  if (/[",\n\r]/.test(v)) {
    return `"${v.replace(/"/g, '""')}"`;
  }
  return v;
}

function downloadCsv(filename: string, header: string[], rows: string[][]): void {
  const lines = [header.map(csvCell).join(','), ...rows.map((r) => r.map(csvCell).join(','))];
  const blob = new Blob(['﻿' + lines.join('\r\n')], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function downloadJson(filename: string, obj: unknown): void {
  const blob = new Blob([JSON.stringify(obj, null, 2)], { type: 'application/json;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function verdictPillTone(v: Verdict): 'ok' | 'warn' | 'err' | 'info' | 'muted' {
  switch (v) {
    case 'PASS': return 'ok';
    case 'PARTIAL': return 'warn';
    case 'FAIL': return 'err';
    case 'ERROR': return 'err';
    case 'PENDING': return 'muted';
    default: return 'muted';
  }
}

// ── Component ──────────────────────────────────────────────────────────────

export function OfacRubricRunner(): JSX.Element {
  const client = useApiClient();
  const [rubricText, setRubricText] = useState<string>('');
  const [parseError, setParseError] = useState<string | null>(null);
  const [running, setRunning] = useState<boolean>(false);
  const [progress, setProgress] = useState<{ done: number; total: number }>({ done: 0, total: 0 });
  const [run, setRun] = useState<RubricRun | null>(null);
  const [toast, setToast] = useState<CrudToast | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const parsedRubric = useMemo<Rubric | null>(() => {
    if (!rubricText.trim()) {
      setParseError(null);
      return null;
    }
    try {
      const obj = JSON.parse(rubricText) as Rubric;
      if (!obj || typeof obj !== 'object' || !Array.isArray(obj.tests)) {
        setParseError('Rubric must include a `tests` array.');
        return null;
      }
      const bad = obj.tests.findIndex((t) => !t || typeof t.comName !== 'string' || !t.comName.trim());
      if (bad >= 0) {
        setParseError(`Test #${bad + 1} is missing a non-empty \`comName\`.`);
        return null;
      }
      setParseError(null);
      return obj;
    } catch (e: unknown) {
      setParseError(e instanceof Error ? e.message : 'JSON parse failed.');
      return null;
    }
  }, [rubricText]);

  const loadSample = useCallback((): void => {
    setRubricText(JSON.stringify(SAMPLE_RUBRIC, null, 2));
    setRun(null);
  }, []);

  const onDrop = useCallback((evt: DragEvent<HTMLTextAreaElement | HTMLDivElement>): void => {
    evt.preventDefault();
    const file = evt.dataTransfer?.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (): void => {
      const text = String(reader.result ?? '');
      setRubricText(text);
      setRun(null);
    };
    reader.readAsText(file);
  }, []);

  const onFilePick = useCallback((evt: ChangeEvent<HTMLInputElement>): void => {
    const file = evt.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (): void => {
      const text = String(reader.result ?? '');
      setRubricText(text);
      setRun(null);
    };
    reader.readAsText(file);
  }, []);

  const runRubric = useCallback(async (): Promise<void> => {
    if (!parsedRubric || running) return;
    setRunning(true);
    const startedAt = new Date().toISOString();
    const t0 = performance.now();
    const tests = parsedRubric.tests;
    setProgress({ done: 0, total: tests.length });

    const results: TestResult[] = [];

    for (let i = 0; i < tests.length; i++) {
      const t = tests[i];
      const testId = t.id ?? `t${i + 1}`;
      const comNameType = t.comNameType ?? 'Indv';

      try {
        const jhaResp = (await client.jxrl.ofacSrch({
          ComName: t.comName,
          ComNameType: comNameType,
        })) as Record<string, unknown>;

        const arr = (jhaResp?.OFACSrchArray as unknown);
        const hits = Array.isArray(arr) ? arr : [];

        if (hits.length === 0) {
          results.push({
            testId,
            comName: t.comName,
            jhaHits: 0,
            jhaSummaryRowsSkipped: 0,
            matches: [],
            overall: 'FAIL',
            jhaError: 'JHA returned 0 hits — rubric cannot validate without at least one AliasId.',
          });
        } else {
          const matches: MatchRow[] = [];
          let summaryRowsSkipped = 0;

          // SLS lookup per AliasId — sequential to keep ordering stable + avoid
          // hammering Treasury with a burst that could earn us a rate-limit cap.
          for (const raw of hits) {
            const r = (raw ?? {}) as Record<string, unknown>;
            const aliasId = String(r.AliasId ?? '').trim();
            const aliasName = typeof r.AliasName === 'string' ? r.AliasName : '';
            const matchType = typeof r.OFACMatchType === 'string' ? r.OFACMatchType : '';

            // Bill 2026-05-09 — JHA's OFACSrch returns summary rows with no
            // AliasId (typically OFACMatchType="OFACName"). They aren't real
            // match candidates we can resolve; treat them as count-only,
            // not as a FAIL row that drags the test down.
            if (!aliasId) {
              summaryRowsSkipped++;
              continue;
            }

            try {
              const sls = (await client.retail.dev.ofacSls(aliasId)) as OfacSlsLookupResponse;
              if (!sls.success || !sls.found || !sls.entity) {
                matches.push({
                  testId,
                  comName: t.comName,
                  jhaAliasId: aliasId,
                  jhaAliasName: aliasName,
                  jhaMatchType: matchType,
                  slsFound: false,
                  slsPrimaryName: '',
                  slsAliasNames: '',
                  slsPrograms: '',
                  slsDob: '',
                  slsGender: '',
                  slsPlaceOfBirth: '',
                  nameMatch: 'NONE',
                  verdict: 'FAIL',
                  error: sls.error ?? sls.note ?? 'SLS returned no entity for this AliasId.',
                });
                continue;
              }

              const ent = sls.entity;
              const allNames = ent.names ?? [];
              const primary = ent.primaryName ?? '';
              const aliasList = allNames
                .filter((n) => !n.isPrimary)
                .map((n) => n.fullName ?? '')
                .filter((s) => s.length > 0)
                .join('; ');
              const programs = (ent.programs ?? []).join(', ');
              const nm = classifyNameMatch(aliasName, allNames);
              const programOk = (ent.programs?.length ?? 0) > 0;

              const verdict: Verdict = nm !== 'NONE' && programOk ? 'PASS' : 'PARTIAL';

              matches.push({
                testId,
                comName: t.comName,
                jhaAliasId: aliasId,
                jhaAliasName: aliasName,
                jhaMatchType: matchType,
                slsFound: true,
                slsPrimaryName: primary,
                slsAliasNames: aliasList,
                slsPrograms: programs,
                slsDob: ent.dateOfBirth ?? '',
                slsGender: ent.gender ?? '',
                slsPlaceOfBirth: ent.placeOfBirth ?? '',
                nameMatch: nm,
                verdict,
              });
            } catch (slsErr: unknown) {
              const msg = slsErr instanceof Error ? slsErr.message : 'SLS lookup failed.';
              matches.push({
                testId,
                comName: t.comName,
                jhaAliasId: aliasId,
                jhaAliasName: aliasName,
                jhaMatchType: matchType,
                slsFound: false,
                slsPrimaryName: '',
                slsAliasNames: '',
                slsPrograms: '',
                slsDob: '',
                slsGender: '',
                slsPlaceOfBirth: '',
                nameMatch: 'NONE',
                verdict: 'ERROR',
                error: msg,
              });
            }
          }

          // Test-level overall verdict: PASS if any match PASSes AND
          // expected (alias id, name) constraints are met (when supplied).
          const anyPass = matches.some((m) => m.verdict === 'PASS');
          const anyPartial = matches.some((m) => m.verdict === 'PARTIAL');
          const anyError = matches.some((m) => m.verdict === 'ERROR');
          const allFail = matches.length > 0
            && matches.every((m) => m.verdict === 'FAIL' || m.verdict === 'ERROR');

          let expectedAliasIdMet: boolean | undefined;
          if (t.expectAliasIdContains && t.expectAliasIdContains.length > 0) {
            const ids = matches.map((m) => Number(m.jhaAliasId)).filter((n) => Number.isFinite(n));
            expectedAliasIdMet = t.expectAliasIdContains.some((want) => ids.includes(want));
          }
          let expectedNameMet: boolean | undefined;
          if (t.expectNameContains) {
            const want = normalizeName(t.expectNameContains);
            expectedNameMet = matches.some((m) =>
              normalizeName(m.slsPrimaryName).includes(want) ||
              normalizeName(m.slsAliasNames).includes(want)
            );
          }
          const expectsMet =
            (expectedAliasIdMet === undefined || expectedAliasIdMet) &&
            (expectedNameMet === undefined || expectedNameMet);

          let overall: Verdict;
          if (matches.length === 0) {
            // Every JHA row was a summary row — no AliasIds to score.
            // Still surface as FAIL so the test draws attention; the per-test
            // header will show "(N summary-only rows skipped)" so it's clear
            // the rubric ran but had nothing to assert against.
            overall = 'FAIL';
          } else if (allFail) overall = 'FAIL';
          else if (anyError && !anyPass) overall = 'ERROR';
          else if (anyPass && expectsMet) overall = 'PASS';
          else if (anyPartial || (anyPass && !expectsMet)) overall = 'PARTIAL';
          else overall = 'FAIL';

          results.push({
            testId,
            comName: t.comName,
            jhaHits: hits.length,
            jhaSummaryRowsSkipped: summaryRowsSkipped,
            matches,
            overall,
            ...(matches.length === 0 ? { jhaError: `JHA returned ${hits.length} hit row(s) but every one was a summary row (no AliasId) — nothing to score against Treasury SLS.` } : {}),
            ...(expectedAliasIdMet === undefined ? {} : { expectedAliasIdMet }),
            ...(expectedNameMet === undefined ? {} : { expectedNameMet }),
          });
        }
      } catch (err: unknown) {
        const msg = err instanceof Error ? err.message : 'JHA OFACSrch failed.';
        results.push({
          testId,
          comName: t.comName,
          jhaHits: 0,
          jhaSummaryRowsSkipped: 0,
          matches: [],
          overall: 'ERROR',
          jhaError: msg,
        });
      }

      setProgress({ done: i + 1, total: tests.length });
      // Live-render partial results.
      const partial: RubricRun = {
        startedAt,
        rubricName: parsedRubric.name ?? 'unnamed rubric',
        results: [...results],
        summary: summarize(results, performance.now() - t0),
      };
      setRun(partial);
    }

    const t1 = performance.now();
    const finalRun: RubricRun = {
      startedAt,
      finishedAt: new Date().toISOString(),
      rubricName: parsedRubric.name ?? 'unnamed rubric',
      results,
      summary: summarize(results, t1 - t0),
    };
    setRun(finalRun);
    setRunning(false);
    setToast({
      tone: finalRun.summary.fail + finalRun.summary.error === 0 ? 'ok' : 'warn',
      text: `Rubric run complete in ${Math.round(finalRun.summary.durationMs)}ms — ` +
        `${finalRun.summary.pass}/${finalRun.summary.total} PASS, ` +
        `${finalRun.summary.partial} PARTIAL, ${finalRun.summary.fail} FAIL, ` +
        `${finalRun.summary.error} ERROR.`,
    });
  }, [client, parsedRubric, running]);

  const exportCsv = useCallback((): void => {
    if (!run) return;
    const header = [
      'testId', 'comName', 'overall',
      'jhaAliasId', 'jhaAliasName', 'jhaMatchType',
      'slsFound', 'slsPrimaryName', 'slsAliasNames', 'slsPrograms',
      'slsDob', 'slsGender', 'slsPlaceOfBirth',
      'nameMatch', 'verdict', 'error',
    ];
    const rows: string[][] = [];
    for (const t of run.results) {
      if (t.matches.length === 0) {
        rows.push([
          t.testId, t.comName, t.overall,
          '', '', '',
          'false', '', '', '',
          '', '', '',
          'NONE', t.overall, t.jhaError ?? '',
        ]);
      } else {
        for (const m of t.matches) {
          rows.push([
            t.testId, t.comName, t.overall,
            m.jhaAliasId, m.jhaAliasName, m.jhaMatchType,
            m.slsFound ? 'true' : 'false',
            m.slsPrimaryName, m.slsAliasNames, m.slsPrograms,
            m.slsDob, m.slsGender, m.slsPlaceOfBirth,
            m.nameMatch, m.verdict, m.error ?? '',
          ]);
        }
      }
    }
    const stamp = new Date().toISOString().replace(/[:.]/g, '-');
    downloadCsv(`ofac-rubric-${stamp}.csv`, header, rows);
  }, [run]);

  const exportJson = useCallback((): void => {
    if (!run) return;
    const stamp = new Date().toISOString().replace(/[:.]/g, '-');
    downloadJson(`ofac-rubric-${stamp}.json`, run);
  }, [run]);

  return (
    <CrudPage>
      <CrudHeader
        title="OFAC Rubric Runner (Dev)"
        intro="Verifies the JHA OFACSrch ↔ Treasury SLS mapping by running a JSON rubric of test names through the production wire (App → Function → JHA, then Function → Treasury SLS). Use this before promoting any Risk Mgmt automation that depends on the AliasId mapping."
        badge={{ tone: 'warn', label: 'DEV — gated by dev.tools.enabled' }}
        actions={
          <>
            <Button variant="ghost" onClick={loadSample} disabled={running}>Load sample rubric</Button>
            <Button variant="ghost" onClick={() => fileInputRef.current?.click()} disabled={running}>
              Open file…
            </Button>
            <input
              ref={fileInputRef}
              type="file"
              accept="application/json,.json"
              style={{ display: 'none' }}
              onChange={onFilePick}
            />
            <Button variant="primary" onClick={() => void runRubric()} disabled={!parsedRubric || running}>
              {running ? `Running ${progress.done}/${progress.total}…` : 'Run rubric'}
            </Button>
          </>
        }
      />

      <CrudToastBanner toast={toast} onDismiss={() => setToast(null)} />

      <Banner tone="info">
        Drop a rubric JSON file into the textarea below, paste one in, or click <em>Load sample rubric</em>.
        Each test fires a real JHA OFAC search; for every AliasId returned, the runner pulls the
        Treasury SLS entity through the Function and scores the mapping. Server gates: SuperAdmin
        role plus <code>dev.tools.enabled</code> SystemSetting (decoupled from
        <code>dev.mode.enabled</code> — does NOT affect cart routing).
      </Banner>

      {/* ── Rubric input ─────────────────────────────────────────────── */}
      <section style={sectionStyle}>
        <h2 style={h2Style}>Rubric input</h2>
        <textarea
          value={rubricText}
          onChange={(e) => setRubricText(e.target.value)}
          onDrop={onDrop}
          onDragOver={(e) => e.preventDefault()}
          placeholder='{ "name": "OFAC enrichment rubric v1", "tests": [ { "comName": "Mohammad Daoud Muzzamil", "comNameType": "Indv" } ] }'
          spellCheck={false}
          style={{
            width: '100%',
            minHeight: 220,
            fontFamily: 'Consolas, Menlo, monospace',
            fontSize: 12,
            padding: 10,
            border: '1px solid var(--ink-200, #e5e7eb)',
            borderRadius: 6,
            background: 'var(--surface-card, #fff)',
            color: 'var(--ink-90, #1e2530)',
            lineHeight: 1.45,
            resize: 'vertical',
          }}
        />
        {parseError && <Banner tone="err" title="Rubric JSON invalid">{parseError}</Banner>}
        {!parseError && parsedRubric && (
          <div style={{ marginTop: 8, fontSize: 12, color: 'var(--ink-70, #6b7280)' }}>
            Loaded <strong>{parsedRubric.name ?? 'unnamed rubric'}</strong> — {parsedRubric.tests.length} test{parsedRubric.tests.length === 1 ? '' : 's'} ready.
          </div>
        )}
      </section>

      {/* ── Summary ──────────────────────────────────────────────────── */}
      {run && (
        <section style={sectionStyle}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
            <h2 style={h2Style}>Run summary</h2>
            <Pill variant="info">{run.rubricName}</Pill>
            <Pill variant="muted">started {new Date(run.startedAt).toLocaleTimeString()}</Pill>
            <Pill variant="muted">{Math.round(run.summary.durationMs)}ms</Pill>
            <div style={{ flex: 1 }} />
            <Button variant="ghost" onClick={exportCsv} disabled={running}>Download CSV</Button>
            <Button variant="ghost" onClick={exportJson} disabled={running}>Download JSON</Button>
          </div>
          <div style={{ display: 'flex', gap: 12, marginTop: 12, flexWrap: 'wrap' }}>
            <Stat label="Total tests" value={run.summary.total} />
            <Stat label="PASS" value={run.summary.pass} tone="ok" />
            <Stat label="PARTIAL" value={run.summary.partial} tone="warn" />
            <Stat label="FAIL" value={run.summary.fail} tone="err" />
            <Stat label="ERROR" value={run.summary.error} tone="err" />
            <Stat label="Pass rate"
              value={run.summary.total === 0 ? '—'
                : `${Math.round((run.summary.pass / run.summary.total) * 100)}%`} />
          </div>
        </section>
      )}

      {/* ── Per-test result tables ─────────────────────────────────── */}
      {run && (
        <section style={sectionStyle}>
          <h2 style={h2Style}>Test results</h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            {run.results.map((t) => (
              <TestResultCard key={t.testId} test={t} />
            ))}
          </div>
        </section>
      )}
    </CrudPage>
  );
}

// ── Helpers ────────────────────────────────────────────────────────────────

function summarize(results: TestResult[], durationMs: number): RubricRun['summary'] {
  const total = results.length;
  let pass = 0, partial = 0, fail = 0, error = 0;
  for (const r of results) {
    if (r.overall === 'PASS') pass++;
    else if (r.overall === 'PARTIAL') partial++;
    else if (r.overall === 'FAIL') fail++;
    else if (r.overall === 'ERROR') error++;
  }
  return { total, pass, partial, fail, error, durationMs };
}

const sectionStyle: CSSProperties = {
  marginTop: 18,
  padding: '14px 16px',
  border: '1px solid var(--ink-100, #e5e7eb)',
  borderRadius: 8,
  background: 'var(--surface-card, #fff)',
};

const h2Style: CSSProperties = {
  margin: 0,
  fontSize: 15,
  fontWeight: 600,
  color: 'var(--ink-90, #1e2530)',
};

function Stat({
  label, value, tone,
}: {
  label: string;
  value: number | string;
  tone?: 'ok' | 'warn' | 'err';
}): JSX.Element {
  const color = tone === 'ok' ? 'var(--ok-700, #166534)'
    : tone === 'warn' ? 'var(--warn-700, #92400e)'
    : tone === 'err' ? 'var(--err-700, #991b1b)'
    : 'var(--ink-90, #1e2530)';
  return (
    <div style={{
      padding: '8px 12px',
      border: '1px solid var(--ink-100, #e5e7eb)',
      borderRadius: 6,
      minWidth: 110,
    }}>
      <div style={{ fontSize: 11, color: 'var(--ink-70, #6b7280)', textTransform: 'uppercase', letterSpacing: 0.4 }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 600, color, marginTop: 2 }}>{value}</div>
    </div>
  );
}

function TestResultCard({ test }: { test: TestResult }): JSX.Element {
  return (
    <div style={{
      border: '1px solid var(--ink-100, #e5e7eb)',
      borderRadius: 6,
      overflow: 'hidden',
    }}>
      <header style={{
        padding: '10px 14px',
        background: 'var(--surface-subtle, #f9fafb)',
        borderBottom: '1px solid var(--ink-100, #e5e7eb)',
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        flexWrap: 'wrap',
      }}>
        <Pill variant={verdictPillTone(test.overall)}>{test.overall}</Pill>
        <strong style={{ fontSize: 13 }}>{test.testId}</strong>
        <span style={{ fontSize: 13, color: 'var(--ink-90, #1e2530)' }}>{test.comName}</span>
        <span style={{ fontSize: 12, color: 'var(--ink-70, #6b7280)' }}>
          JHA hits: {test.jhaHits}
          {test.jhaSummaryRowsSkipped > 0 ? (
            <span style={{ marginLeft: 6 }}>
              ({test.matches.length} scoreable, {test.jhaSummaryRowsSkipped} summary-only)
            </span>
          ) : null}
        </span>
        {test.expectedAliasIdMet === false && (
          <Pill variant="warn">expected AliasId not in response</Pill>
        )}
        {test.expectedNameMet === false && (
          <Pill variant="warn">expected name not in SLS results</Pill>
        )}
      </header>
      {test.jhaError && (
        <div style={{ padding: '10px 14px' }}>
          <Banner tone="err" title="JHA error">{test.jhaError}</Banner>
        </div>
      )}
      {test.matches.length > 0 && (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead>
              <tr style={{ background: 'var(--ink-050, #f3f4f6)' }}>
                <Hcell>Verdict</Hcell>
                <Hcell>JHA AliasId</Hcell>
                <Hcell>JHA AliasName</Hcell>
                <Hcell>JHA Match</Hcell>
                <Hcell>SLS Primary</Hcell>
                <Hcell>SLS Programs</Hcell>
                <Hcell>SLS DOB / POB / Gender</Hcell>
                <Hcell>Name Match</Hcell>
                <Hcell>Notes</Hcell>
              </tr>
            </thead>
            <tbody>
              {test.matches.map((m, idx) => (
                <tr key={`${m.jhaAliasId}-${idx}`} style={{ borderTop: '1px solid var(--ink-100, #e5e7eb)' }}>
                  <Cell><Pill variant={verdictPillTone(m.verdict)}>{m.verdict}</Pill></Cell>
                  <Cell mono>{m.jhaAliasId || '—'}</Cell>
                  <Cell>{m.jhaAliasName || '—'}</Cell>
                  <Cell>{m.jhaMatchType || '—'}</Cell>
                  <Cell>{m.slsPrimaryName || (m.slsFound ? '(unnamed)' : '—')}</Cell>
                  <Cell>{m.slsPrograms || '—'}</Cell>
                  <Cell>
                    <div>{m.slsDob || '—'}</div>
                    <div style={{ color: 'var(--ink-70, #6b7280)' }}>{m.slsPlaceOfBirth || ''}</div>
                    <div style={{ color: 'var(--ink-70, #6b7280)' }}>{m.slsGender || ''}</div>
                  </Cell>
                  <Cell>{m.nameMatch}</Cell>
                  <Cell>
                    {m.error
                      ? <span style={{ color: 'var(--err-700, #991b1b)' }}>{m.error}</span>
                      : (m.slsAliasNames
                        ? <span style={{ color: 'var(--ink-70, #6b7280)' }}>AKAs: {m.slsAliasNames}</span>
                        : '—')}
                  </Cell>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function Hcell({ children }: { children: ReactNode }): JSX.Element {
  return (
    <th style={{
      textAlign: 'left',
      padding: '8px 10px',
      fontWeight: 600,
      fontSize: 11,
      color: 'var(--ink-70, #6b7280)',
      textTransform: 'uppercase',
      letterSpacing: 0.4,
      borderBottom: '1px solid var(--ink-100, #e5e7eb)',
    }}>{children}</th>
  );
}

function Cell({ children, mono }: { children: ReactNode; mono?: boolean }): JSX.Element {
  return (
    <td style={{
      padding: '8px 10px',
      verticalAlign: 'top',
      ...(mono ? { fontFamily: 'Consolas, Menlo, monospace' } : {}),
    }}>{children}</td>
  );
}
